<template>
  <v-card class="password-rules-display" flat variant="outlined">
    <v-card-text class="pa-4">
      <p class="font-weight-bold mb-3">{{ $t('common.PasswordRulesDisplay.intro') }}</p>
      <ul class="password-rules-list ml-4" data-locator="password-rules-list">
        <template v-for="rule in passwordRules" :key="rule.key">
          <li class="password-rule-item" :data-locator="`password-rule-${rule.key}`">
            <app-material-icon
              v-if="hasPassword && rule.isValid !== undefined"
              :name="rule.isValid ? 'check' : 'close'"
              :color="rule.isValid ? 'success' : 'error'"
              size="sm"
              class="mr-2 inline-icon"
              :data-locator="`password-rule-icon-${rule.key}`"
              :data-valid="`${rule.isValid}`"
            />{{ rule.message }}
            <ul v-if="rule.nestedRules" class="ml-6">
              <li 
                v-for="nestedRule in rule.nestedRules" 
                :key="nestedRule.key"
                :data-locator="`password-rule-${nestedRule.key}`"
                class="password-rule-item"
              >
                <app-material-icon
                  v-if="hasPassword && nestedRule.isValid !== undefined"
                  :name="nestedRule.isValid ? 'check' : 'close'"
                  :color="nestedRule.isValid ? 'success' : 'error'"
                  size="sm"
                  class="mr-2 inline-icon"
                  :data-locator="`password-rule-icon-${nestedRule.key}`"
                  :data-valid="`${nestedRule.isValid}`"
                />{{ nestedRule.message }}
              </li>
            </ul>
          </li>
        </template>
      </ul>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { defineComponent, computed, type PropType } from 'vue'

import type { ValidationRule } from '@vuelidate/core'

export const lengthAndNoSpaces: ValidationRule = (value: string): boolean => {
  if (!value) return true
  return value.length >= 8 && value.length <= 50 && !/\s/.test(value)
}

export const threeCategories: ValidationRule = (value: string): boolean => {
  if (!value) return true
  const categories = [
    /[A-Z]/.test(value), // Uppercase letters
    /[a-z]/.test(value), // Lowercase letters
    /[0-9]/.test(value), // Numbers
    /[!#$%&'*+\-/=?^_`{|}~.@]/.test(value), // Special characters
  ]

  return categories.filter(Boolean).length >= 3
}

interface PasswordRule {
  key: string
  message: string
  isValid: boolean | undefined
  nestedRules?: PasswordRule[]
}

/**
 * Password Rules Display Component
 * Self-contained component with Vuelidate 2.0 validators for password validation
 * Also exports validators for external use
 * @displayName PasswordRulesDisplay
 */
export default defineComponent({
  name: 'PasswordRulesDisplay',
  props: {
    password: {
      type: String,
      default: '',
    },
    /**
     * External validation for checking if password is not one of the 5 previous passwords
     * undefined = check pending, true = valid, false = invalid
     */
    isNotPreviousPassword: {
      type: Boolean as PropType<boolean | undefined>,
      default: undefined,
    },
  },

  setup(props: any) {
    const passwordValue = computed(() => props.password)
    
    const rules = {
      password: {
        required,
        lengthAndNoSpaces,
        threeCategories,
      },
    }

    const v$ = useVuelidate(rules, { password: passwordValue })

    return { v$ }
  },
  computed: {
    hasPassword(): boolean {
      return !!(this.password && this.password.length > 0)
    },
    /**
     * Check if core password rules are valid (excludes notPrevious rule)
     * This can be used by parent components to enable/disable form submission
     */
    areRulesValid(): boolean {
      return !this.v$.password.lengthAndNoSpaces.$invalid && 
             !this.v$.password.threeCategories.$invalid
    },
    /**
     * Check if all rules including notPrevious are valid
     */
    areAllRulesValid(): boolean {
      return this.areRulesValid && this.isNotPreviousPassword === true
    },
    /**
     * Check if the previous password check is still pending
     */
    isPreviousPasswordCheckPending(): boolean {
      return this.isNotPreviousPassword === undefined
    },
    passwordRules(): PasswordRule[] {
      const password = this.password
      const hasUppercase = password ? /[A-Z]/.test(password) : false
      const hasLowercase = password ? /[a-z]/.test(password) : false
      const hasNumbers = password ? /[0-9]/.test(password) : false
      const hasSpecialChars = password ? /[!#$%&'*+\-/=?^_`{|}~.@]/.test(password) : false
      
      const categoryRules: PasswordRule[] = [
        {
          key: 'hasUppercase',
          message: this.$t('common.PasswordRulesDisplay.hasUppercase'),
          isValid: hasUppercase,
        },
        {
          key: 'hasLowercase', 
          message: this.$t('common.PasswordRulesDisplay.hasLowercase'),
          isValid: hasLowercase,
        },
        {
          key: 'hasNumbers',
          message: this.$t('common.PasswordRulesDisplay.hasNumbers'),
          isValid: hasNumbers,
        },
        {
          key: 'hasSpecialChars',
          message: this.$t('common.PasswordRulesDisplay.hasSpecialChars'),
          isValid: hasSpecialChars,
        },
      ]
      
      return [
        {
          key: 'lengthAndSpaces',
          message: this.$t('common.PasswordRulesDisplay.lengthAndNoSpaces'),
          isValid: !this.v$.password.lengthAndNoSpaces.$invalid,
        },
        {
          key: 'notPrevious',
          message: this.$t('common.PasswordRulesDisplay.notPreviousPassword'),
          isValid: this.isNotPreviousPassword,
        },
        {
          key: 'categories',
          message: this.$t('common.PasswordRulesDisplay.threeCategories'),
          isValid: !this.v$.password.threeCategories.$invalid,
          nestedRules: categoryRules,
        },
      ]
    },
  },
  watch: {
    areRulesValid(newVal) {
      this.$emit('rules-validity-changed', newVal)
    },
  },
  methods: {
    /**
     * Public method to check if core rules are valid
     * @returns {boolean} True if length and categories rules are valid
     */
    checkRulesValidity(): boolean {
      return this.areRulesValid
    },
    /**
     * Public method to get detailed validation state
     * @returns {object} Object with validation details
     */
    getValidationState(): {
      areRulesValid: boolean
      areAllRulesValid: boolean
      lengthValid: boolean
      categoriesValid: boolean
      notPreviousValid: boolean | undefined
      isPreviousPasswordCheckPending: boolean
    } {
      return {
        areRulesValid: this.areRulesValid,
        areAllRulesValid: this.areAllRulesValid,
        lengthValid: !this.v$.password.lengthAndNoSpaces.$invalid,
        categoriesValid: !this.v$.password.threeCategories.$invalid,
        notPreviousValid: this.isNotPreviousPassword,
        isPreviousPasswordCheckPending: this.isPreviousPasswordCheckPending,
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.inline-icon {
  vertical-align: bottom;
  margin-top: 2px;
}
</style>